import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { getAllSystems } from "../../../service/systems"
import { getAllSessions, type Session } from "../../../service/sessions"
import { getAllClasses, type Class } from "../../../service/classes"
import { selectBranches, type Branch } from "../../../core/data/redux/slice/branchesSlice"
import {
  listGradeConfigs,
  createGradeConfig,
  updateGradeConfig,
  deleteGradeConfig,
  getGradeConfigScopes,
  setGradeConfigScopes,
  type GradeConfig,
  type GradeRow,
} from "../../../service/grade-config"

// 2026-10-04 RMS-parity rewrite: a Grade Scale is global now (create once
// per session, no longer tied to one campus) -- where it applies is
// managed separately via the Scopes modal, same split and same UI pattern
// as Term Management. Matches RMS's own two-step flow too: "New scale"
// creates just the header (name/session/board/description), grade bands
// are added afterward via a separate "Rows" editor -- not crammed into one
// modal with the header fields.

type SystemOpt = { _id: string; name: string; code?: string; style?: "local" | "cambridge" }

const SYSTEM_PRIORITY: Record<string, number> = { MATRIC: 1, OLEVEL: 2, AKU: 3, COLLEGE: 4 }
const byPriority = (systems: SystemOpt[]) =>
  [...systems].sort((a, b) => (SYSTEM_PRIORITY[a.code || ""] ?? 99) - (SYSTEM_PRIORITY[b.code || ""] ?? 99))

const classSystemId = (c: Class): string => {
  const s = (c as any).system
  return s && typeof s === "object" ? s._id : s
}

const CertGradeConfig = () => {
  const branches = useSelector(selectBranches) as Branch[]

  const [items, setItems] = useState<GradeConfig[]>([])
  const [systems, setSystems] = useState<SystemOpt[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [classes, setClasses] = useState<Class[]>([])
  const [loading, setLoading] = useState(false)
  const [editingHeader, setEditingHeader] = useState<GradeConfig | "new" | null>(null)
  const [editingRows, setEditingRows] = useState<GradeConfig | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Filters (RMS shows these as Session / Board system / Class dropdowns
  // above the table). Filtered client-side against the already-loaded list
  // -- the scopes already included per row (for the Scopes column) are
  // enough to answer "does this scale apply to this class", no extra
  // backend query needed.
  const [filterSession, setFilterSession] = useState("")
  const [filterSystem, setFilterSystem] = useState("")
  const [filterClass, setFilterClass] = useState("")

  // Session is a separate document per campus sharing only its title (same
  // as everywhere else this applies) -- the picker only needs the distinct
  // title, not one option per campus's underlying document.
  const uniqueSessionsByTitle = useMemo(() => {
    const seen = new Set<string>()
    const out: Session[] = []
    for (const s of sessions) {
      if (seen.has(s.title)) continue
      seen.add(s.title)
      out.push(s)
    }
    return out
  }, [sessions])

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await listGradeConfigs({ limit: 200 })
      setItems(res?.data?.configs || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load grade scales")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
    getAllSystems()
      .then((r: any) => setSystems(byPriority(r?.data || r?.systems || r || [])))
      .catch(() => setSystems([]))
    getAllSessions({ page: 1, limit: 1000, all: true })
      .then(r => setSessions(r.sessions ?? []))
      .catch(() => setSessions([]))
    getAllClasses({ page: 1, limit: 1000, all: "true" })
      .then(r => setClasses(r.classes ?? []))
      .catch(() => setClasses([]))
  }, [])

  useEffect(() => { setPage(1) }, [items.length, filterSession, filterSystem, filterClass])

  const sysName = (s: GradeConfig["system"]) => (typeof s === "string" ? "" : s?.name) || ""
  const sessName = (s: GradeConfig["session"]) => (typeof s === "string" ? "" : s?.title) || ""

  // The Session dropdown shows one option per distinct title (see
  // uniqueSessionsByTitle) but picking one used to filter by that single
  // document's exact _id -- every other campus's own same-titled session
  // document (and every grade scale scoped to it) silently vanished from
  // the list. Resolve the picked option back to its title and compare
  // titles instead, same as the dropdown already dedupes by.
  const filterSessionTitle = useMemo(
    () => sessions.find(s => s._id === filterSession)?.title || "",
    [sessions, filterSession]
  )

  const filteredItems = useMemo(() => {
    return items.filter(it => {
      if (filterSession && sessName(it.session) !== filterSessionTitle) return false
      if (filterSystem && (typeof it.system === "string" ? it.system : it.system?._id) !== filterSystem) return false
      if (filterClass && !(it.scopes || []).some(s => !s.classId || s.classId === filterClass)) return false
      return true
    })
  }, [items, filterSession, filterSessionTitle, filterSystem, filterClass])

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await deleteGradeConfig(deleteId)
      toast.success("Deleted")
      setDeleteId(null)
      fetchItems()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Delete failed")
    }
  }

  // ── Scopes modal (same pattern as Term Management) ─────────────────────
  const [scopingConfig, setScopingConfig] = useState<GradeConfig | null>(null)
  const [scopeRows, setScopeRows] = useState<Array<{
    branchId: string; branchName: string; systemId: string; systemName: string;
    apply: boolean; allClasses: boolean; classIds: string[];
  }>>([])
  const [scopesLoading, setScopesLoading] = useState(false)
  const [savingScopes, setSavingScopes] = useState(false)

  const ensureClassesLoaded = async () => {
    if (classes.length) return
    const r = await getAllClasses({ page: 1, limit: 1000, all: "true" })
    setClasses(r.classes ?? [])
  }

  const openScopes = async (config: GradeConfig) => {
    setScopingConfig(config)
    setScopesLoading(true)
    try {
      const [existingScopes] = await Promise.all([getGradeConfigScopes(config._id), ensureClassesLoaded()])

      const rows: typeof scopeRows = []
      for (const b of branches) {
        for (const s of b.systems || []) {
          const matching = existingScopes.filter((sc) => {
            const scBranch = typeof sc.branch === "object" ? sc.branch._id : sc.branch
            const scSystem = typeof sc.systemId === "object" ? sc.systemId._id : sc.systemId
            return scBranch === b._id && scSystem === s._id
          })
          const applied = matching.length > 0
          const allClasses = applied && matching.some((sc) => !sc.classId)
          const classIds = matching
            .filter((sc) => sc.classId)
            .map((sc) => (typeof sc.classId === "object" ? sc.classId!._id : (sc.classId as string)))
          rows.push({ branchId: b._id, branchName: b.name, systemId: s._id, systemName: s.name, apply: applied, allClasses: applied ? allClasses : true, classIds })
        }
      }
      setScopeRows(rows)
    } catch {
      toast.error("Error loading scopes")
      setScopingConfig(null)
    } finally {
      setScopesLoading(false)
    }
  }

  const closeScopes = () => { setScopingConfig(null); setScopeRows([]) }

  const saveScopes = async () => {
    if (!scopingConfig) return
    setSavingScopes(true)
    try {
      const payload: { branch: string; systemId: string; classId?: string }[] = []
      for (const row of scopeRows) {
        if (!row.apply) continue
        if (row.allClasses || row.classIds.length === 0) {
          payload.push({ branch: row.branchId, systemId: row.systemId })
        } else {
          for (const cId of row.classIds) payload.push({ branch: row.branchId, systemId: row.systemId, classId: cId })
        }
      }
      await setGradeConfigScopes(scopingConfig._id, payload)
      toast.success("Scopes saved")
      closeScopes()
      fetchItems()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Error saving scopes")
    } finally {
      setSavingScopes(false)
    }
  }

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Session", dataIndex: "session", render: (s: any) => sessName(s) || "—" },
    { title: "Board", dataIndex: "system", render: (s: any) => sysName(s) || "—" },
    {
      title: "Scopes",
      dataIndex: "scopes",
      render: (scopes: GradeConfig["scopes"]) =>
        scopes?.length ? (
          <div className="d-flex flex-wrap gap-1">
            {scopes.map((s, i) => (
              <span key={i} className="badge bg-light text-dark border">
                {[s.branchCode || s.branchName, s.systemCode || s.systemName, s.className].filter(Boolean).join("·")}
              </span>
            ))}
          </div>
        ) : <span className="text-muted">—</span>,
    },
    { title: "Rows", dataIndex: "grades", render: (g: GradeRow[]) => (g?.length ? `${g.length} grade${g.length === 1 ? "" : "s"}` : <span className="text-muted">— none yet</span>) },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (v: boolean) =>
        v === false
          ? <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
          : <span className="badge bg-success-transparent text-success">Active</span>,
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (_: any, r: GradeConfig) => (
        <div className="d-flex gap-2">
          <Link to="#" className="btn btn-sm btn-outline-secondary" onClick={() => openScopes(r)}>Scopes</Link>
          <Link to="#" className="btn btn-sm btn-outline-secondary" onClick={() => setEditingRows(r)}>Rows</Link>
          <Link to="#" className="btn btn-sm btn-outline-primary" onClick={() => setEditingHeader(r)}><i className="ti ti-edit" /></Link>
          <Link to="#" className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(r._id)}><i className="ti ti-trash" /></Link>
        </div>
      ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Grade Scales</h3>
            <p className="text-muted mb-0">
              Per session x board system. Create a scale once, then add its grade bands via
              <strong> Rows</strong> and pick which campuses use it via <strong>Scopes</strong>.
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setEditingHeader("new")}>
            <i className="ti ti-square-rounded-plus me-2" />New Grade Scale
          </button>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <label className="form-label mb-1">Session</label>
            <select className="form-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
              <option value="">— Any —</option>
              {uniqueSessionsByTitle.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1">Board system</label>
            <select className="form-select" value={filterSystem} onChange={e => setFilterSystem(e.target.value)}>
              <option value="">— Any —</option>
              {systems.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1">Class</label>
            <select className="form-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
              <option value="">— Any —</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={filteredItems}
              columns={columns}
              loading={loading}
              pagination={{ currentPage: page, limit: pageSize, total: filteredItems.length, onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) } }}
            />
          </div>
        </div>
      </div>

      {editingHeader && (
        <HeaderModal
          editing={editingHeader}
          systems={systems}
          sessions={uniqueSessionsByTitle}
          onClose={() => setEditingHeader(null)}
          onSaved={() => { setEditingHeader(null); fetchItems() }}
        />
      )}

      {editingRows && (
        <RowsModal
          config={editingRows}
          onClose={() => setEditingRows(null)}
          onSaved={() => { setEditingRows(null); fetchItems() }}
        />
      )}

      {deleteId && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center p-4">
                  <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                  <h4 className="mt-2">Delete Grade Scale</h4>
                  <p className="text-muted">Report cards graded with it keep their saved grades. Continue?</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* Scopes modal */}
      {scopingConfig && (
        <>
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Scopes — {scopingConfig.name}</h4>
                  <button type="button" className="btn-close" onClick={closeScopes} />
                </div>
                <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  <p className="text-muted">
                    Pick where this scale applies. Toggle <strong>Apply</strong> on a campus+board row to
                    scope it there. Leave <strong>All classes</strong> on for "any class of this board";
                    turn it off to narrow to specific classes.
                  </p>
                  {scopesLoading ? (
                    <div className="text-center py-4">Loading…</div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {scopeRows.map((row, idx) => (
                        <div key={`${row.branchId}-${row.systemId}`} className="border rounded p-2">
                          <div className="d-flex align-items-center flex-wrap gap-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={row.apply}
                                onChange={() => setScopeRows(prev => prev.map((r, i) => i === idx ? { ...r, apply: !r.apply } : r))}
                              />
                              <label className="form-check-label">
                                {row.branchName} <span className="badge bg-light text-dark">{row.systemName}</span>
                              </label>
                            </div>
                            {row.apply && (
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={row.allClasses}
                                  onChange={() => setScopeRows(prev => prev.map((r, i) => i === idx ? { ...r, allClasses: !r.allClasses } : r))}
                                />
                                <label className="form-check-label">All classes</label>
                              </div>
                            )}
                          </div>
                          {row.apply && !row.allClasses && (
                            <div className="d-flex flex-wrap gap-2 mt-2 ps-4">
                              {classes.filter(c => classSystemId(c) === row.systemId).map(c => (
                                <div className="form-check" key={c._id}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={row.classIds.includes(c._id)}
                                    onChange={() => setScopeRows(prev => prev.map((r, i) => {
                                      if (i !== idx) return r
                                      const classIds = r.classIds.includes(c._id) ? r.classIds.filter(x => x !== c._id) : [...r.classIds, c._id]
                                      return { ...r, classIds }
                                    }))}
                                  />
                                  <label className="form-check-label">{c.name}</label>
                                </div>
                              ))}
                              {classes.filter(c => classSystemId(c) === row.systemId).length === 0 && (
                                <span className="text-muted">No classes found for this board.</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {scopeRows.length === 0 && <p className="text-muted">No campuses with a board system configured.</p>}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light me-2" onClick={closeScopes}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveScopes} disabled={savingScopes || scopesLoading}>
                    {savingScopes ? "Saving…" : "Save scopes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  )
}

// ── Header modal: Name / Session / Board / Description / Active only --
// matches RMS's own "New scale" modal exactly. Grade bands are a SEPARATE
// step (RowsModal below), not crammed in here.
const HeaderModal = ({
  editing,
  systems,
  sessions,
  onClose,
  onSaved,
}: {
  editing: GradeConfig | "new"
  systems: SystemOpt[]
  sessions: Session[]
  onClose: () => void
  onSaved: () => void
}) => {
  const isEdit = editing !== "new"
  const defaultSystemId = useMemo(() => systems[0]?._id || "", [systems])
  const [name, setName] = useState(isEdit ? editing.name : "")
  const [description, setDescription] = useState(isEdit ? editing.description || "" : "")
  const [session, setSession] = useState(isEdit ? (typeof editing.session === "string" ? editing.session : editing.session?._id) : "")
  const [system, setSystem] = useState(isEdit ? (typeof editing.system === "string" ? editing.system : editing.system?._id) : defaultSystemId)
  const [active, setActive] = useState(isEdit ? editing.isActive !== false : true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (!isEdit && !system && defaultSystemId) setSystem(defaultSystemId) }, [defaultSystemId]) // eslint-disable-line

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { setError("Enter a name"); return }
    if (!session) { setError("Pick a session"); return }
    if (!system) { setError("Pick a system"); return }
    setSaving(true); setError(null)
    try {
      if (isEdit) {
        await updateGradeConfig(editing._id, { name: name.trim(), description: description.trim(), session, system, isActive: active })
        toast.success("Updated")
      } else {
        await createGradeConfig({ name: name.trim(), description: description.trim(), session, system, isActive: active })
        toast.success("Created — now add its grade bands via Rows")
      }
      onSaved()
    } catch (e2: any) {
      setError(e2?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{isEdit ? "Edit grade scale" : "New grade scale"}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. National Board Scale" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Session</label>
                  <select className="form-select" value={session} onChange={e => setSession(e.target.value)}>
                    <option value="">— Select session —</option>
                    {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Board system</label>
                  <select className="form-select" value={system} onChange={e => setSystem(e.target.value)}>
                    <option value="">— Select board system —</option>
                    {systems.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional notes about this scale" />
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="gc_active" checked={active} onChange={e => setActive(e.target.checked)} />
                  <label className="form-check-label" htmlFor="gc_active">Active</label>
                </div>

                {error && <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light me-2" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : isEdit ? "Save" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Rows modal: just the grade-band table, matching RMS's separate "Rows"
// editor -- the header's own fields (name/session/board) aren't touched
// here at all.
const RowsModal = ({
  config,
  onClose,
  onSaved,
}: {
  config: GradeConfig
  onClose: () => void
  onSaved: () => void
}) => {
  const [grades, setGrades] = useState<GradeRow[]>(config.grades.map(g => ({ ...g })))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setRow = (i: number, patch: Partial<GradeRow>) =>
    setGrades(gs => gs.map((g, idx) => (idx === i ? { ...g, ...patch } : g)))
  const addRow = () => setGrades(gs => [...gs, { grade: "", minPercentage: 0, maxPercentage: 0 }])
  const removeRow = (i: number) => setGrades(gs => gs.filter((_, idx) => idx !== i))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const clean = grades.map(g => ({
      grade: g.grade.trim(),
      minPercentage: Number(g.minPercentage),
      maxPercentage: Number(g.maxPercentage),
      ...(g.colorCode ? { colorCode: g.colorCode } : {}),
      ...(g.remarks ? { remarks: g.remarks } : {}),
    })) as GradeRow[]
    setSaving(true); setError(null)
    try {
      await updateGradeConfig(config._id, { grades: clean })
      toast.success("Rows saved")
      onSaved()
    } catch (e2: any) {
      setError(e2?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Grade rows — {config.name}</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-sm align-middle mb-2">
                    <thead className="table-light">
                      <tr>
                        <th>Grade</th><th>From %</th><th>To %</th><th>Remarks</th><th>Colour</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {grades.map((g, i) => (
                        <tr key={i}>
                          <td><input className="form-control form-control-sm" style={{ width: 70 }} value={g.grade} onChange={e => setRow(i, { grade: e.target.value })} /></td>
                          <td><input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 80 }} value={g.minPercentage} onChange={e => setRow(i, { minPercentage: Number(e.target.value) })} /></td>
                          <td><input type="number" step="0.01" className="form-control form-control-sm" style={{ width: 80 }} value={g.maxPercentage} onChange={e => setRow(i, { maxPercentage: Number(e.target.value) })} /></td>
                          <td><input className="form-control form-control-sm" style={{ minWidth: 140 }} value={g.remarks || ""} onChange={e => setRow(i, { remarks: e.target.value })} /></td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <input type="color" className="form-control form-control-sm form-control-color" value={g.colorCode || "#000000"} onChange={e => setRow(i, { colorCode: e.target.value })} />
                              <input className="form-control form-control-sm" style={{ width: 90 }} value={g.colorCode || ""} onChange={e => setRow(i, { colorCode: e.target.value })} placeholder="#000000" />
                            </div>
                          </td>
                          <td><button type="button" className="btn btn-sm btn-link text-danger" onClick={() => removeRow(i)}>Remove</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addRow}><i className="ti ti-plus me-1" />Add row</button>
                <p className="text-muted small mt-2 mb-0">Inclusive on both ends. Sorted top-to-bottom by display order. Ranges can't overlap — gaps are allowed.</p>

                {error && <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light me-2" onClick={onClose} disabled={saving}>Close</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save rows"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default CertGradeConfig
